import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import {
  Avatar,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
  Button,
} from '@mui/material';
import {
  authContext,
  Company,
  CompanyLogo,
  Profile,
} from '@staffedupusa/shared-components';
import dayjs from 'dayjs';
import parse from 'html-react-parser';
import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { GroupedVirtuoso } from 'react-virtuoso';
import PreviewFileModal from '@components/PreviewFileModal/PreviewFileModal';
import { Message, Thread } from '@features/messaging';
import {
  getAvatarFallbackText,
  getDividerText,
} from '@features/messaging/components/MessagesList/utils';
import { lightPurple, white, primaryBlue, borderGrey, lightBlue } from '@styles/colors';
import { normal } from '@styles/fontWeights';
import { grey } from '@mui/material/colors';
import { useGetApplicantApplications } from '@features/applications/hooks/useGetApplicantApplications';
import { useGetEnterpriseApplicantApplications } from '@features/applications/hooks/useGetEnterpriseApplicantApplications';
import { Application } from '@features/applications';
import EnterpriseApplicationViewModalHeader from '@features/applications/components/EnterpriseApplicationViewModalHeader/EnterpriseApplicationViewModalHeader';
import EnterpriseApplicationViewModal from '@features/applications/components/EnterpriseApplicationViewModal/EnterpriseApplicationViewModal';
import ApplicationViewModal from '@features/applications/components/ApplicationViewModal/ApplicationViewModal';

type MessagesListProps = {
  messages: Message[];
  fullHeight?: boolean;
  onEndReached: () => void;
  isLoading: boolean;
  loggedInUserId?: number;
  isApplicant?: boolean | null;
  headerData: Thread | null;
};

const MessagesList: FC<MessagesListProps> = ({
  messages,
  fullHeight,
  onEndReached,
  isLoading,
  headerData,
  isApplicant,
  loggedInUserId,
}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const prevMessagesLengthRef = useRef(messages.length);
  const [lastMessageId, setLastMessageId] = useState(0)
  const { authData } = useContext(authContext);
   const [scrollHeight, setScrollHeight] = useState(0);
  const [selectedFilePath, setSelectedFilePath] = useState<string | null>(null);
  const theme = useTheme();
  const [errorText, setErrorText] = useState('');
  const isMobile = useMediaQuery(theme.breakpoints.up('sm'));
  const groupedMessages = groupMessages(messages);
  const [application, setApplication] = useState<Application | null>(null);

  if (!messages.length || !groupedMessages.length) {
    return null;
  }
  const { applicantApplicationsData } = useGetApplicantApplications({
    setErrorText,
    id: headerData?.participant1?.id,
    isCompany: authData?.user?.company ? true : false,
  });

  const { enterpriseApplicantApplications } =
    useGetEnterpriseApplicantApplications({
      setErrorText,
      id: headerData?.participant1?.id,
      company_id: headerData?.company?.id,
      isEnterprise: authData?.user?.enterprise ? true : false,
    });

  const handleViewApplication = () => {
    !isApplicant &&
      setApplication(
        authData?.user?.enterprise
          ? (enterpriseApplicantApplications?.object?.[0] ?? null)
          : authData.user?.company
            ? applicantApplicationsData?.object?.[0]
            : null,
      );
  };
  useEffect(() => {
    if (scrollRef.current) {
      setScrollHeight(scrollRef.current.scrollHeight);
    }
  }, [messages.length]);

  // Maintain scroll position when new messages are loaded
  useEffect(() => {
    if (lastMessageId !== messages[messages.length - 1].id){
      setLastMessageId(messages[messages.length - 1].id)
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    }
    if (scrollRef.current && messages.length > prevMessagesLengthRef.current && messages.length > 10) {
      const newScrollHeight = scrollRef.current.scrollHeight;
      const scrollDiff = newScrollHeight - scrollHeight;
      scrollRef.current.scrollTop = scrollDiff;
      prevMessagesLengthRef.current = messages.length;
    }
  }, [messages.length, lastMessageId, messages[messages.length - 1].id]);
  // Initial scroll to bottom
  useEffect(() => {
    if (scrollRef.current && messages.length > 0) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, []);

  const handleScroll = useCallback(
    (e: { target: any }) => {
      const element = e.target;
      if (element.scrollTop === 0) {
        onEndReached();
      }
    },
    [onEndReached],
  );
  const handleFileClick = useCallback((file_path: any) => {
    setSelectedFilePath(file_path || null);
  }, []);

  const updateRating = (id: number, newRating: number) => {
    if (application && application.id === id) {
      setApplication((prevApp) =>
        prevApp ? { ...prevApp, rating: newRating } : null,
      );
    }
  };

  function checkIsApplicant(user: Profile & { is_admin?: boolean }) {
    return (
      user &&
      !user.company &&
      !user.enterprise &&
      !user.is_admin &&
      !user.is_rockerbox
    );
  }

  function alignmentCheck(message: Message) {
    return (
      (message.sender.company || message.sender.enterprise) &&
      (authData?.user?.company || authData?.user?.enterprise) ||
      (isApplicant && checkIsApplicant(message.sender))
    );
  }

  const renderPreviewFileModal = useMemo(() => {
    if (!selectedFilePath) return null;

    return (
      <PreviewFileModal
        filePath={selectedFilePath}
        handleClose={() => setSelectedFilePath(null)}
      />
    );
  }, [selectedFilePath]);
  return (
    <Stack spacing={1} sx={{ minHeight: 'calc(100% - 376px)' }}>
      {isMobile && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 3,
            py: 3,
            borderBottom: '1px solid rgba(128, 129, 140, .25)',
            width: '100%',
            backgroundColor: white,
            zIndex: '2',
          }}
        >
          {headerData && (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ width: '100%' }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <ListItemAvatar sx={{ minWidth: { xs: 0, sm: 'unset' } }}>
                  <Avatar
                    src={
                      isApplicant && headerData.company.s_logo_file
                        ? headerData.company.s_logo_file
                        : !isApplicant && headerData.participant1?.photo
                          ? headerData.participant1.photo
                          : ''
                    }
                    sx={{
                      width: '50px',
                      height: '50px',
                      marginRight: '8px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                    }}
                    onClick={handleViewApplication}
                  />
                </ListItemAvatar>
                <Stack alignItems="flex-start">
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: '600',
                      fontSize: '20px',
                      // padding: '8px 16px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      display: 'inline-block',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                    onClick={handleViewApplication}
                  >
                    {isApplicant
                      ? headerData.company.s_name
                      : headerData.participant1?.name}
                  </Typography>
                  {authData?.user?.enterprise ? (
                    <Typography>
                      Applied to <b>{headerData?.company?.s_name}</b>
                    </Typography>
                  ) : null}
                </Stack>
              </Stack>
            </Stack>
          )}
        </Box>
      )}
      {isLoading && (
        <Box textAlign="center">
          <CircularProgress color="success" />
        </Box>
      )}
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{
          height: fullHeight ? '100%' : 300,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '4px',
          },
        }}
      >
        {groupedMessages.map((group, groupIndex) => {
            const uniqueMessages = new Set();
              const filteredGroup = group.filter((message) => {
                const uniqueKey = `${message.sender.id}-${message.message}-${message.dt_timestamp}`;
                if (uniqueMessages.has(uniqueKey)) {
                  return false;
                }
                uniqueMessages.add(uniqueKey);
                return true;
              });
          return (
            <React.Fragment key={groupIndex}>
              <Divider
                component="li"
                sx={{
                  fontSize: '10px',
                  color: 'rgba(0, 0, 0, .75)',
                  borderColor: '#fff',
                  maxWidth: '86%',
                  margin: '10px auto',
                  '&::before': {
                    borderColor: 'rgba(128, 129, 140, 0)',
                    zIndex: '-1',
                  },
                  '&::after': {
                    borderColor: 'rgba(128, 129, 140, 0)',
                    zIndex: '-1',
                  },
                }}
              >
                <span
                  style={{
                    backgroundColor: borderGrey,
                    padding: '2px 5px',
                    display: 'inline-block',
                    borderRadius: '4px',
                    color: 'text.secondary',
                    boxShadow: '0 1px 2px rgba(0, 0, 0, .15)',
                  }}
                >
                  {getDividerText(group[0].dt_timestamp)}
                </span>
              </Divider>
              {filteredGroup.map((message, messageIndex) => {
                const { image, bgcolor, color } =
                  getAvatarImageAndColor(message);
                return (
                  <ListItem
                    sx={{
                      py: 2,
                      alignItems: 'end',
                      flexDirection:
                        alignmentCheck(message)
                          ? 'row-reverse'
                          : '',
                    }}
                  >
                    <ListItemAvatar
                      sx={{
                        position: 'relative',
                        top: '-20px',
                        minWidth: 'unset',
                      }}
                    >
                      {alignmentCheck(message) ? (
                        <Box
                          sx={{
                            width: '38px',
                            height: '38px',
                            marginLeft:
                              alignmentCheck(message)
                                ? '10px'
                                : '0px',
                            marginRight:
                              alignmentCheck(message)
                                ? '0px'
                                : '10px',
                            borderRadius: 5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '5px',
                            backgroundColor: 'rgba(128, 129, 140, .15)',
                          }}
                        >
                          <CompanyLogo logoUrl={image} maxWidth="30px" />
                        </Box>
                      ) : (
                        <Avatar
                          src={image}
                          sx={{
                            bgcolor,
                            color,
                            width: '38px',
                            height: '38px',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '600',
                            letterSpacing: '0.5px',
                            marginLeft:
                              alignmentCheck(message)
                                ? '10px'
                                : '0px',
                            marginRight:
                              alignmentCheck(message)
                                ? '0px'
                                : '10px',
                          }}
                        >
                          {getAvatarFallbackText(message?.sender?.name || '')}
                        </Avatar>
                      )}
                    </ListItemAvatar>
                    <ListItemText
                      sx={{
                        position: 'relative',
                        mt: 0,
                        '& p': {
                          mt: 0,
                        },
                        textAlign:
                          alignmentCheck(message) ? 'right' : '',
                      }}
                      primary={
                        <>
                          {message.message && (
                            <Typography
                              sx={{
                                background:
                                  alignmentCheck(message)
                                    ? lightBlue
                                    : 'rgba(128, 129, 140, .15)',
                                maxWidth: 'calc(80%)',
                                fontSize: '13px',
                                borderRadius:
                                  alignmentCheck(message)
                                    ? '6px 6px 0 6px'
                                    : '6px 6px 6px 0',
                                padding: '9px 12px',
                                color:
                                  alignmentCheck(message)
                                    ? '#000'
                                    : '#000',
                                wordWrap: 'break-word',
                                width: 'fit-content',
                                marginLeft:
                                  alignmentCheck(message)
                                    ? 'auto'
                                    : '',
                                textAlign:
                                  alignmentCheck(message)
                                    ? 'right'
                                    : 'left',
                                '& p:last-child': {
                                  mb: 0,
                                },
                              }}
                            >
                              {parse(message.message)}
                            </Typography>
                          )}
                          {message.file && (
                            <IconButton
                              sx={{
                                padding: 0,
                                marginTop: 2,
                                color: primaryBlue,
                                '&:hover': {
                                  background: 'transparent',
                                  textDecoration: 'none',
                                },
                                '& .MuiTypography-root': {
                                  borderBottom: '2px solid transparent',
                                  transition: 'border-color 0.2s',
                                },
                                '&:hover .MuiTypography-root': {
                                  borderBottomColor: primaryBlue,
                                },
                              }}
                              onClick={() =>
                                handleFileClick(message.file?.file_path || null)
                              }
                            >
                              {alignmentCheck(message) ? (
                                <>
                                  <Typography
                                    sx={{
                                      padding: '2px 10px',
                                      fontSize: '13px',
                                    }}
                                  >
                                    {message.file.name}
                                  </Typography>
                                  <InsertDriveFileIcon
                                    sx={{ marginLeft: '4px', fontSize: '18px' }}
                                  />
                                </>
                              ) : (
                                <>
                                  <InsertDriveFileIcon
                                    sx={{ marginLeft: '4px', fontSize: '18px' }}
                                  />
                                  <Typography
                                    sx={{
                                      padding: '2px 10px',
                                      fontSize: '13px',
                                    }}
                                  >
                                    {message.file.name}
                                  </Typography>
                                </>
                              )}
                            </IconButton>
                          )}
                          <Typography
                            sx={{
                              fontSize: '11px',
                              color: 'text.secondary',
                              display: 'block',
                              marginTop: '2px',
                            }}
                            component="span"
                            fontWeight={normal}
                          >
                            {dayjs(message?.dt_timestamp).format('h:mm A')}
                          </Typography>
                        </>
                      }
                      disableTypography
                    />
                    {renderPreviewFileModal}
                  </ListItem>
                );
              })}
            </React.Fragment>
          );})}
      </Box>

      {/* Modals - Same as original */}
      {renderPreviewFileModal}
      {application && enterpriseApplicantApplications && (
        <EnterpriseApplicationViewModal
          handleClose={() => setApplication(null)}
          application={application}
          searchValue=""
          setApplication={setApplication}
          selectedCompanyId={application?.job_listing?.company?.id}
          updateRating={updateRating}
        />
      )}
      {application && applicantApplicationsData && (
        <ApplicationViewModal
          handleClose={() => setApplication(null)}
          application={application}
          searchValue=""
          setApplication={setApplication}
          updateRating={updateRating}
        />
      )}
    </Stack>
  );
};

const getAvatarImageAndColor = (
  message: Message,
): { image: string; bgcolor: string; color: string } => {
  const image = message?.sender?.photo || message?.sender?.company?.s_logo_file;

  if (image) {
    return {
      image,
      color: 'inherit',
      bgcolor: 'transparent',
    };
  }

  return {
    image: '',
    color: white,
    bgcolor: message?.sender?.company ? lightPurple : 'success.main',
  };
};

const groupMessages = (messages: Message[]): Message[][] => {
  const groupedMessages: Message[][] = [];
  let currentDate: string | null = null;
  let currentGroup: Message[] = [];

  messages.forEach((message) => {
    const messageDate = dayjs(message.dt_timestamp).format('YYYY-MM-DD');

    if (messageDate !== currentDate) {
      currentGroup = [];
      groupedMessages.push(currentGroup);
      currentDate = messageDate;
    }

    currentGroup.push(message);
  });

  return groupedMessages;
};

export default MessagesList;
